import { useEffect, useRef, useState } from 'react';

const chips = ['Answers 24/7', 'Qualifies the Job', 'Books Into Your Calendar'];

type GHLWidget = HTMLElement & {
  open?: () => void;
  show?: () => void;
  openWidget?: () => void;
  shadowRoot: ShadowRoot | null;
};

const STICKY_H = 68; // base px — actual height read dynamically below
const LAUNCHER_H = 72; // approx GHL launcher button height + gap

function getStickyBarHeight(): number {
  const el = document.querySelector<HTMLElement>('.sticky-bottom-bar');
  if (el) return el.offsetHeight;
  // Fallback: base + safe area
  const sab = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sab') || '0');
  return STICKY_H + (isNaN(sab) ? 0 : sab);
}

// ─────────────────────────────────────────────
// DEFINITIVE widget constraint — mobile only.
// 
// ROOT CAUSE HISTORY: GHL's <chat-widget> web component applies its own
// default CSS that can make the host element fill a large portion of the
// screen (position:fixed with wide/tall container). Previous fixes either
// (a) made it explicitly full-screen (broke scroll) or (b) only overrode
// bottom+z-index without touching GHL's own width/position defaults
// (GHL's own styles still blocked touch).
//
// THIS FIX:
//  1. Forces `pointer-events: none` on the HOST element so GHL's container
//     can NEVER intercept touch events regardless of its size/position.
//  2. Forces explicit corner positioning so the element itself is small.
//  3. Inside shadow DOM, forces `pointer-events: auto` on all interactive
//     children (button, iframe) so the launcher + chat panel still work.
//
// stackIndex: 0 = voice widget, 1 = chat widget (staggered vertically).
// ─────────────────────────────────────────────
function constrainWidget(widget: HTMLElement, stackIndex: number = 0) {
  const vv = window.visualViewport;
  const kbH = Math.max(0, window.innerHeight - (vv?.height ?? window.innerHeight));
  const isMobile = window.innerWidth <= 768;

  const stickyH = isMobile ? getStickyBarHeight() : 16;
  const bottom = stickyH + kbH + (isMobile ? stackIndex * LAUNCHER_H : 0);
  const right = isMobile ? 16 : 24;

  // ── Host element: explicit corner positioning + pointer-events NONE ──
  // Setting pointer-events:none means this element can NEVER intercept
  // touch/click events that belong to the rest of the page, regardless
  // of how large or positioned GHL makes it. The shadow DOM children
  // explicitly re-enable pointer-events so the widget still functions.
  widget.style.setProperty('position', 'fixed', 'important');
  widget.style.setProperty('bottom', `${bottom}px`, 'important');
  widget.style.setProperty('right', `${right}px`, 'important');
  widget.style.setProperty('left', 'auto', 'important');
  widget.style.setProperty('top', 'auto', 'important');
  widget.style.setProperty('width', 'auto', 'important');
  widget.style.setProperty('max-width', 'calc(100vw - 32px)', 'important');
  widget.style.setProperty('height', 'auto', 'important');
  widget.style.setProperty('z-index', `${9999 + stackIndex}`, 'important');
  // CRITICAL: Makes the host transparent to ALL pointer events.
  // GHL's container can be any size — it won't steal touch from the page.
  widget.style.setProperty('pointer-events', 'none', 'important');
  // Prevent the host element from ever overflowing the viewport width.
  // This is the document-level equivalent of the <head> style — belt AND braces.
  widget.style.setProperty('max-width', '100vw', 'important');
  widget.style.setProperty('overflow', 'hidden', 'important');
  widget.style.setProperty('box-sizing', 'border-box', 'important');

  // ── Shadow DOM: re-enable pointer-events on interactive children ──
  // Without this, children would inherit pointer-events:none and the
  // launcher button / chat panel would be un-tappable.
  const shadow = (widget as GHLWidget).shadowRoot;
  if (shadow) {
    let innerStyle = shadow.querySelector<HTMLStyleElement>('#ghl-mobile-inner');
    if (!innerStyle) {
      innerStyle = document.createElement('style');
      innerStyle.id = 'ghl-mobile-inner';
      shadow.prepend(innerStyle);
    }
    innerStyle.textContent = `
      :host {
        position: fixed !important;
        bottom: ${bottom}px !important;
        right: ${right}px !important;
        left: auto !important;
        top: auto !important;
        width: auto !important;
        max-width: calc(100vw - 32px) !important;
        overflow: hidden !important;
        box-sizing: border-box !important;
        height: auto !important;
        z-index: ${9999 + stackIndex} !important;
        pointer-events: none !important;
      }
      /*
       * CRITICAL: Only re-enable pointer-events on the actual interactive
       * elements (buttons, links, inputs, iframes). Do NOT include generic
       * divs with class names like container/wrapper/panel/chat — those are
       * often large transparent overlays that block touch/scroll on the page.
       */
      button,
      [role="button"],
      a,
      input,
      textarea,
      select,
      label {
        pointer-events: auto !important;
      }
      iframe {
        pointer-events: auto !important;
        max-width: calc(100vw - 32px) !important;
        max-height: calc(100svh - ${bottom + 10}px) !important;
        overflow: hidden !important;
        border: none !important;
      }
      /* Hide greeting/notification bubbles by common class patterns */
      [class*="greeting"],
      [class*="Greeting"],
      [class*="notification"],
      [class*="Notification"],
      [class*="bubble-popup"],
      [class*="BubblePopup"],
      [class*="chat-popup"],
      [class*="ChatPopup"],
      [class*="opener-greeting"],
      [class*="OpenerGreeting"],
      [class*="launcher-label"],
      [class*="launcher-text"],
      [class*="unread-badge"],
      [class*="UnreadBadge"],
      [data-testid*="greeting"],
      [data-testid*="notification"],
      [data-testid*="bubble"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
    `;
  }
}

export default function VoiceDemo() {
  const sectionRef      = useRef<HTMLDivElement>(null);
  const widgetRef       = useRef<GHLWidget | null>(null);
  const tapHintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [widgetReady, setWidgetReady] = useState(false);
  const [tapHint, setTapHint] = useState('');

  // Reveal animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px 80px 0px' }
    );
    const reveals = sectionRef.current?.querySelectorAll('.reveal');
    reveals?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // ── Minimal document-level CSS: only hide GHL greeting popups ──
  // NOTE: We do NOT touch body/html overflow here — setting overflow-x: hidden
  // on body can kill iOS Safari vertical scrolling, making the page appear
  // broken (only hero visible, rest unreachable by touch).
  useEffect(() => {
    const styleId = 'ghl-global-fix';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        /* Hide GHL greeting/notification popup — these float over the page */
        chat-widget::part(notification-container),
        chat-widget::part(greeting-container),
        chat-widget::part(chat-popup),
        chat-widget::part(chat-greeting),
        chat-widget::part(unread-badge),
        chat-widget::part(notification),
        chat-widget::part(greeting),
        chat-widget::part(popup),
        chat-widget::part(tooltip),
        chat-widget::part(bubble-popup),
        chat-widget::part(opener-greeting),
        chat-widget::part(launcher-label),
        chat-widget::part(launcher-text) {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
        body > div[id*="chat-popup"],
        body > div[class*="chat-popup"],
        body > div[id*="greeting"],
        body > div[class*="greeting"],
        body > iframe[id*="chat-popup"],
        body > iframe[id*="greeting"] {
          display: none !important;
        }

        /* Tablet */
        @media (min-width: 769px) and (max-width: 1024px) {
          chat-widget {
            right: 16px !important;
            bottom: 16px !important;
          }
        }
      `;
      document.head.appendChild(style);
    }

    // ── Aggressively hide greeting bubbles inside shadow DOM ──
    // GHL renders greeting popups inside shadow roots — ::part() only works
    // if the widget explicitly exposes those parts. We inject a style tag
    // directly into each shadow root to hide them by class/attribute patterns.
    const hideGreetingsInShadow = () => {
      document.querySelectorAll<HTMLElement>('chat-widget').forEach((widget) => {
        const shadow = widget.shadowRoot;
        if (!shadow) return;
        const greetingStyleId = 'ghl-greeting-hide';
        if (shadow.querySelector(`#${greetingStyleId}`)) return;
        const s = document.createElement('style');
        s.id = greetingStyleId;
        s.textContent = `
          /* Hide greeting/notification bubbles by common class patterns */
          [class*="greeting"],
          [class*="Greeting"],
          [class*="notification"],
          [class*="Notification"],
          [class*="bubble-popup"],
          [class*="BubblePopup"],
          [class*="chat-popup"],
          [class*="ChatPopup"],
          [class*="opener-greeting"],
          [class*="OpenerGreeting"],
          [class*="launcher-label"],
          [class*="launcher-text"],
          [class*="unread-badge"],
          [class*="UnreadBadge"],
          [data-testid*="greeting"],
          [data-testid*="notification"],
          [data-testid*="bubble"],
          [aria-label*="greeting"],
          [aria-label*="notification"] {
            display: none !important;
            visibility: hidden !important;
            opacity: 0 !important;
            pointer-events: none !important;
          }
        `;
        shadow.prepend(s);
      });
    };

    // Run immediately and watch for new widgets
    hideGreetingsInShadow();
    const greetingObserver = new MutationObserver(hideGreetingsInShadow);
    greetingObserver.observe(document.body, { childList: true, subtree: false });

    return () => greetingObserver.disconnect();
  }, []);

  // ── JS-driven constraint system (mobile) ──
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const runConstrain = () => {
      // Pass the DOM insertion order as stackIndex so widgets are
      // staggered vertically — voice (index 0) at base bottom,
      // chat (index 1) at base + LAUNCHER_H, etc.
      const widgets = Array.from(document.querySelectorAll<HTMLElement>('chat-widget'));
      widgets.forEach((w, i) => constrainWidget(w, i));
    };

    // Body observer: detect new chat-widget elements added by GHL.
    // NOTE: We intentionally do NOT observe shadow DOM — constrainWidget writes
    // innerStyle.textContent inside the shadow root, which would create an
    // infinite MutationObserver feedback loop and freeze the browser.
    // visualViewport + setInterval below are sufficient for repositioning.
    const bodyObserver = new MutationObserver((mutations) => {
      let changed = false;
      mutations.forEach((m) => {
        m.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement && node.tagName?.toLowerCase() === 'chat-widget') {
            changed = true;
          }
        });
      });
      if (changed) runConstrain();
    });
    bodyObserver.observe(document.body, { childList: true });

    // Apply to widgets already in DOM (pass correct stackIndex for each)
    document.querySelectorAll<HTMLElement>('chat-widget').forEach((w, i) => {
      constrainWidget(w, i);
    });

    // Keyboard: visualViewport fires resize/scroll when soft keyboard opens
    const vv = window.visualViewport;
    vv?.addEventListener('resize', runConstrain);
    vv?.addEventListener('scroll', runConstrain);

    // Safety-net: re-apply a few times during initial load in case GHL
    // re-applies its own styles. Run 6 times at 1.5s intervals then stop.
    // We do NOT run this indefinitely — frequent style overrides can cause
    // performance jank and make mobile feel sluggish.
    let ticks = 0;
    const interval = setInterval(() => {
      runConstrain();
      ticks++;
      if (ticks >= 6) clearInterval(interval);
    }, 1500);

    return () => {
      bodyObserver.disconnect();
      vv?.removeEventListener('resize', runConstrain);
      vv?.removeEventListener('scroll', runConstrain);
      clearInterval(interval);
    };
  }, []);

  // Load GHL script + watch for the <chat-widget> web component to appear
  useEffect(() => {
    const preExistingWidgets = new Set(Array.from(document.querySelectorAll('chat-widget')));

    let resolveVoiceReady!: (el: HTMLElement) => void;
    (window as Record<string, unknown>)['__ghlVoiceWidgetReady'] = new Promise<HTMLElement>(
      (resolve) => { resolveVoiceReady = resolve; }
    );

    if (!document.querySelector('script[data-widget-id="69c1aefc1642816c0ada56e9"]')) {
      const script = document.createElement('script');
      script.src = 'https://widgets.leadconnectorhq.com/loader.js';
      script.setAttribute('data-resources-url', 'https://widgets.leadconnectorhq.com/chat-widget/loader.js');
      script.setAttribute('data-widget-id', '69c1aefc1642816c0ada56e9');
      document.body.appendChild(script);
    }

    const checkForWidget = () => {
      const allWidgets = Array.from(document.querySelectorAll<GHLWidget>('chat-widget'));
      const voiceWidget = allWidgets.find((el) => !preExistingWidgets.has(el as Element));
      if (voiceWidget && !widgetRef.current) {
        widgetRef.current = voiceWidget;
        setWidgetReady(true);
        resolveVoiceReady(voiceWidget);
        return true;
      }
      return false;
    };

    const observer = new MutationObserver(() => { checkForWidget(); });
    observer.observe(document.body, { childList: true, subtree: true });
    const interval = setInterval(() => { if (checkForWidget()) clearInterval(interval); }, 250);
    checkForWidget();

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (tapHintTimerRef.current) clearTimeout(tapHintTimerRef.current);
    };
  }, []);

  const handleOpenWidget = () => {
    const el = widgetRef.current;
    if (!el) {
      // Widget hasn't loaded yet — give the user clear feedback
      setTapHint('Agent is loading… tap again in a moment!');
      if (tapHintTimerRef.current) clearTimeout(tapHintTimerRef.current);
      tapHintTimerRef.current = setTimeout(() => setTapHint(''), 3500);
      return;
    }
    setTapHint('');

    // IMPORTANT: Do NOT call el.open() / el.show() / el.openWidget() — those
    // GHL API methods bypass the normal widget flow and trigger the calendar
    // booking page directly. We simulate a real user tap on the native launcher
    // button so the widget opens in its default conversational/voice mode.
    el.style.setProperty('pointer-events', 'auto', 'important');
    const shadow = el.shadowRoot;
    if (shadow) {
      // Re-enable pointer-events on interactive children so the panel is usable
      let innerStyle = shadow.querySelector<HTMLStyleElement>('#ghl-mobile-inner');
      if (innerStyle) {
        // Patch: ensure pointer-events are auto on the host when open
        innerStyle.textContent = innerStyle.textContent?.replace(
          'pointer-events: none !important;',
          'pointer-events: auto !important;'
        ) ?? innerStyle.textContent;
      }

      // Find and click the widget's OWN native launcher button
      const nativeBtn =
        shadow.querySelector<HTMLElement>('[data-testid="chat-widget-launcher"]') ??
        shadow.querySelector<HTMLElement>('[class*="launcher"]') ??
        shadow.querySelector<HTMLElement>('[class*="chat-button"]') ??
        shadow.querySelector<HTMLElement>('[class*="open-button"]') ??
        shadow.querySelector<HTMLElement>('[class*="toggle"]') ??
        shadow.querySelector<HTMLElement>('button');

      if (nativeBtn) {
        nativeBtn.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
        return;
      }
    }

    // Last resort fallback
    el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
  };

  return (
    <section
      ref={sectionRef}
      className="relative pt-16 pb-24 md:py-16 px-5 lg:px-8 overflow-hidden"
      style={{ backgroundColor: '#080F0C' }}
    >
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: 'linear-gradient(to right, transparent, rgba(10,92,68,0.5), transparent)' }}
      />

      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:gap-16">

          {/* Left: Text */}
          <div className="md:flex-1 mb-8 md:mb-0">
            <div className="reveal opacity-0 mb-3" style={{ transitionDelay: '0ms' }}>
              <span
                className="font-dm font-semibold text-xs tracking-[0.2em] uppercase"
                style={{ color: '#AAFF45', textShadow: '0 0 10px rgba(170,255,69,0.5)' }}
              >
                ◆ SOLUTION 1 — TRY IT NOW
              </span>
            </div>

            <div className="reveal opacity-0 mb-3" style={{ transitionDelay: '80ms' }}>
              <h2
                className="font-bebas text-brand-text"
                style={{ fontSize: 'clamp(38px, 5vw, 56px)' }}
              >
                Talk To The AI.<br />Right Now.
              </h2>
            </div>

            <div className="reveal opacity-0 mb-8 md:mb-6" style={{ transitionDelay: '160ms' }}>
              <p className="font-dm text-brand-grey text-base leading-relaxed">
                Same AI your customers will hear. Enter your name and number — the agent calls you straight back. Pretend you&apos;re a homeowner. See what happens.
              </p>
            </div>

            <div className="reveal opacity-0 flex flex-wrap gap-2" style={{ transitionDelay: '360ms' }}>
              {chips.map((chip, i) => (
                <span
                  key={i}
                  className="font-dm text-xs font-medium px-3 py-1.5 rounded-full"
                  style={{
                    backgroundColor: 'rgba(10,92,68,0.3)',
                    color: '#AAFF45',
                    border: '1px solid rgba(10,92,68,0.6)',
                  }}
                >
                  {chip}
                </span>
              ))}
            </div>
          </div>

          {/* Right: Demo Widget Box */}
          <div className="md:w-[360px] lg:w-[400px] flex-shrink-0">
            <div
              className="reveal opacity-0 rounded-2xl p-6 relative overflow-hidden"
              style={{
                transitionDelay: '240ms',
                backgroundColor: '#0a1812',
                border: '1px solid rgba(10,92,68,0.6)',
                boxShadow: '0 0 40px rgba(10,92,68,0.25), inset 0 0 40px rgba(10,92,68,0.05)',
              }}
            >
              {/* LIVE DEMO badge */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <span
                    className="w-2 h-2 rounded-full animate-live-pulse"
                    style={{ backgroundColor: '#AAFF45', boxShadow: '0 0 6px rgba(170,255,69,0.8)' }}
                  />
                  <span className="font-dm font-bold text-xs tracking-widest uppercase" style={{ color: '#AAFF45' }}>
                    LIVE DEMO
                  </span>
                </div>
                <span className="font-dm text-brand-grey text-xs">Murphy&apos;s Plumbing</span>
              </div>

              {/* Animated mic */}
              <div className="flex justify-center mb-6">
                <div
                  className="relative w-20 h-20 flex items-center justify-center rounded-full"
                  style={{ backgroundColor: 'rgba(10,92,68,0.3)', border: '1px solid rgba(10,92,68,0.5)' }}
                >
                  <span
                    className="absolute inset-0 rounded-full animate-ring-pulse"
                    style={{ border: '2px solid rgba(170,255,69,0.3)' }}
                  />
                  <span
                    className="absolute inset-0 rounded-full animate-ring-pulse"
                    style={{ border: '2px solid rgba(170,255,69,0.2)', animationDelay: '0.6s' }}
                  />
                  <i className="ri-mic-line text-4xl" style={{ color: '#AAFF45' }} />
                </div>
              </div>

              {/* 3-step flow */}
              <div className="flex items-start gap-2 mb-5">
                {['Your Name', 'Your Number', 'AI Calls You'].map((step, i) => (
                  <div key={i} className="flex-1 text-center">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center font-dm font-bold text-xs mx-auto mb-1"
                      style={{
                        backgroundColor: 'rgba(170,255,69,0.15)',
                        border: '1px solid rgba(170,255,69,0.4)',
                        color: '#AAFF45',
                      }}
                    >
                      {i + 1}
                    </div>
                    <p className="font-dm text-brand-grey" style={{ fontSize: '10px' }}>{step}</p>
                  </div>
                ))}
              </div>

              <p className="font-dm text-center text-brand-grey text-sm mb-1 leading-snug">
                Hit the button — enter your{' '}
                <strong style={{ color: '#F5F5F5' }}>name &amp; number</strong>
                <br />and the AI agent calls you right back.
              </p>
              <p className="font-dm text-center text-brand-grey text-xs mb-4 italic">
                No app. No dialling. 100% real AI call.
              </p>

              {/* Attention arrows */}
              <div className="flex flex-col items-center gap-0.5 mb-3">
                <span
                  className="font-dm font-bold tracking-[0.18em] uppercase"
                  style={{ fontSize: '11px', color: '#AAFF45', textShadow: '0 0 10px rgba(170,255,69,0.7)' }}
                >
                  ↓ &nbsp; TAP HERE TO TRY IT &nbsp; ↓
                </span>
                <div className="flex items-center gap-4">
                  <i
                    className="ri-arrow-down-fill animate-bounce"
                    style={{ color: '#AAFF45', fontSize: '26px', filter: 'drop-shadow(0 0 6px rgba(170,255,69,0.8))' }}
                  />
                  <i
                    className="ri-cursor-fill animate-bounce"
                    style={{ color: '#AAFF45', fontSize: '22px', animationDelay: '0.15s', filter: 'drop-shadow(0 0 6px rgba(170,255,69,0.8))' }}
                  />
                  <i
                    className="ri-arrow-down-fill animate-bounce"
                    style={{ color: '#AAFF45', fontSize: '26px', animationDelay: '0.3s', filter: 'drop-shadow(0 0 6px rgba(170,255,69,0.8))' }}
                  />
                </div>
              </div>

              {/* CTA button */}
              <div
                className="relative mb-4"
                style={{ padding: '3px', borderRadius: '16px', background: 'linear-gradient(135deg, rgba(170,255,69,0.9) 0%, rgba(10,92,68,0.6) 50%, rgba(170,255,69,0.9) 100%)' }}
              >
                <div
                  className="absolute -inset-1 rounded-2xl animate-pulse pointer-events-none"
                  style={{ background: 'rgba(170,255,69,0.2)', borderRadius: '18px' }}
                />
                <button
                  type="button"
                  onClick={handleOpenWidget}
                  className="whitespace-nowrap flex items-center justify-center gap-2 sm:gap-3 w-full font-dm font-bold rounded-xl cursor-pointer transition-all duration-200 hover:brightness-110 active:scale-95"
                  style={{
                    backgroundColor: '#AAFF45',
                    color: '#080F0C',
                    fontSize: 'clamp(15px, 4.5vw, 19px)',
                    padding: '18px 16px',
                    minHeight: '66px',
                    border: 'none',
                    borderRadius: '13px',
                  }}
                >
                  <span
                    className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full flex-shrink-0"
                    style={{ backgroundColor: 'rgba(8,15,12,0.2)' }}
                  >
                    <i className="ri-phone-fill text-base sm:text-lg" style={{ color: '#080F0C' }} />
                  </span>
                  <span>Talk to the AI Agent</span>
                  <i className="ri-arrow-right-line text-base sm:text-lg" style={{ color: 'rgba(8,15,12,0.6)' }} />
                </button>
              </div>

              {/* Ready indicator */}
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{
                    backgroundColor: widgetReady ? '#AAFF45' : '#A0A8A4',
                    boxShadow: widgetReady ? '0 0 4px rgba(170,255,69,0.8)' : 'none',
                    transition: 'all 0.4s ease',
                  }}
                />
                <p className="font-dm text-xs" style={{ color: widgetReady ? 'rgba(170,255,69,0.7)' : '#A0A8A4' }}>
                  {tapHint || (widgetReady ? 'Agent is live and ready' : 'Loading agent…')}
                </p>
              </div>

              <p className="font-dm text-center text-xs italic" style={{ color: 'rgba(160,168,164,0.55)' }}>
                AI answers, qualifies &amp; books. This is{' '}
                <strong style={{ color: '#F5F5F5' }}>REAL.</strong>
              </p>

              <div
                className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none"
                style={{ background: 'linear-gradient(to top, rgba(10,92,68,0.15), transparent)' }}
              />
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
